import styles from "@/components/dock.module.css";
import PresetShortcut from "@/components/preset-shortcut";
import Shortcut from "@/components/shortcut";
import { getPresetShortcutDetails } from "@/data/preset-shortcut-details";
import useShortcuts from "@/hooks/use-shortcuts";
import * as signals from "@/signals";
import { BookmarkInfo, PositionInfo } from "@/types";
import { getNumberKeyFromCode, openWebPage } from "@/utils";
import {
	horizontalListSortingStrategy,
	SortableContext,
} from "@dnd-kit/sortable";
import clsx from "clsx";
import { Fragment, JSX, useEffect, useMemo, useState } from "react";

function Dock(): JSX.Element {
	const [isShortcutTitleShown, setIsShortcutTitleShown] =
		useState<boolean>(false);
	const [shortcutTitlePosition, setShortcutTitlePosition] =
		useState<PositionInfo>({
			left: 0,
			top: 0,
		});
	const [shortcutTitleText, setShortcutTitleText] = useState<string>("");

	const { data } = useShortcuts();

	const allPresetDetails = getPresetShortcutDetails();
	const userShortcuts: BookmarkInfo[] = useMemo(
		() => [
			{
				icon: "",
				itemType: "preset",
				title: "allShortcuts",
				url: null,
			},
			...(data?.dockItems || []),
		],
		[data?.dockItems],
	);

	const dragIds: string[] = [];
	const shortcutUniqueKeys = new Set<string | number>(); // avoid duplicate items
	const shortcutElements = userShortcuts.map((item, index) => {
		const key = item.id || item.title || index;
		if (shortcutUniqueKeys.has(key)) {
			console.warn("Duplicate key skipped:", key);
			return null;
		}
		shortcutUniqueKeys.add(key);

		const dragId = "dock-" + key.toString() + "-" + item.itemType;
		dragIds.push(dragId);

		return (
			<Fragment key={key}>
				{item.itemType === "bookmark" && (
					<Shortcut
						dragId={dragId}
						isDraggable={index !== 0}
						isInDock={true}
						item={item}
						setIsShortcutTitleShown={setIsShortcutTitleShown}
						setShortcutTitlePosition={setShortcutTitlePosition}
						setShortcutTitleText={setShortcutTitleText}
					/>
				)}
				{item.itemType === "preset" && item.title && (
					<PresetShortcut
						dragId={dragId}
						isDraggable={index !== 0}
						isInDock={true}
						id={item.id}
						setIsShortcutTitleShown={setIsShortcutTitleShown}
						setShortcutTitlePosition={setShortcutTitlePosition}
						setShortcutTitleText={setShortcutTitleText}
						title={item.title}
					/>
				)}
			</Fragment>
		);
	});

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent): void => {
			const areAnyPopupsShown = Object.values(
				signals.arePopupsShown.value,
			).some((value) => value);
			const isInputFocused =
				document.activeElement instanceof HTMLElement &&
				(document.activeElement.tagName === "INPUT" ||
					document.activeElement.tagName === "TEXTAREA");
			if (
				!areAnyPopupsShown &&
				!isInputFocused &&
				!signals.isSearchFocused.value &&
				!document.getElementById("headlessui-portal-root") &&
				data?.dockItems &&
				// Only when Ctrl is not pressed to avoid conflicts with browser shortcuts
				!event.ctrlKey &&
				!event.metaKey &&
				event.key !== " " &&
				(event.code.startsWith("Digit") ||
					event.code.startsWith("Numpad"))
			) {
				event.preventDefault();
				const index = getNumberKeyFromCode(event.code);
				if (index === null) {
					return;
				}
				const item = data.dockItems[index];
				if (item) {
					if (item.itemType === "bookmark") {
						if (item.url) {
							openWebPage(item.url);
						}
					} else if (item.itemType === "preset") {
						const preset = allPresetDetails[item.title];
						if (preset) {
							if (preset.onClick) {
								void preset.onClick();
							} else if (preset.url) {
								openWebPage(preset.url);
							}
						}
					}
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return (): void => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [allPresetDetails, data?.dockItems]);

	if (
		!signals.generalSettings.value.isDockEnabled ||
		shortcutElements.length <= 1
	) {
		return <></>;
	}
	return (
		<>
			<div
				className={clsx(
					signals.generalSettings.value.isLiteModeEnabled && "lite",
					styles["dock"],
					signals.generalSettings.value.isFooterEnabled &&
						styles["with-footer"],
				)}
			>
				<SortableContext
					items={dragIds}
					strategy={horizontalListSortingStrategy}
				>
					{shortcutElements}
				</SortableContext>
			</div>
			{isShortcutTitleShown && (
				<div
					className={styles["shortcut-title"]}
					style={{
						left: shortcutTitlePosition.left,
						top: shortcutTitlePosition.top,
					}}
				>
					{shortcutTitleText}
				</div>
			)}
		</>
	);
}

export default Dock;
